import { api, ApiError } from "./api";
import { readDraft, writeDraft, listDrafts } from "./draft-storage";
export type ResponseDraft = { moduleId: string; position: number; questionId: string; questionRevision: number; value: string | null; flagged: boolean; note: string; eliminated: string[] };
type Position = { moduleId: string; position: number };
type Action = "pause" | "resume" | "finish-module" | "start-next-module" | "skip-break" | "complete";
type Batch = { version: number; requestId: string; responses: ResponseDraft[]; position?: Position; action?: Action };
type State = { version: number; pending: Record<string, ResponseDraft>; position?: Position; uncertain?: Batch };
const fingerprint = (value: Pick<ResponseDraft, "questionId" | "value" | "flagged" | "note" | "eliminated">) => JSON.stringify([value.questionId, value.value, value.flagged, value.note, [...value.eliminated].sort()]);
const hasResponses = (state: State) => Boolean(Object.keys(state.pending).length || state.uncertain);
export const SAVE_CHECKPOINT_MS = 30_000;
export const SAVE_INTERVAL_MS = 5_000;
const key = (value: Position) => `${value.moduleId}:${value.position}`;
export class DurableSaveQueue {
  private state: State;
  private baseline = new Map<string, string>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private timerDue = Infinity;
  private editedAt: number | null = null;
  private lastRequestAt = -Infinity;
  private failures = 0;
  private closed = false;
  private storageKey: string;
  private running: Promise<void> | null = null;
  private persistence = Promise.resolve();
  private storageAvailable = true;
  conflict = false;
  private retryAt = 0;
  private channel: BroadcastChannel | null = null;
  constructor(private scope: string, private attemptId: string, version: number, private report: (state: string, warning?: string) => void) {
    let tabId = crypto.randomUUID();
    try { const existing = sessionStorage.getItem("sat-recovery-tab"); if (existing) tabId = existing as typeof tabId; else sessionStorage.setItem("sat-recovery-tab", tabId); } catch { /* Memory-only identity when browser storage is disabled. */ }
    this.storageKey = `${scope}:${tabId}`;
    this.state = { version, pending: {} };
    if (typeof BroadcastChannel !== "undefined") {
      this.channel = new BroadcastChannel(`sat:${scope}`);
      this.channel.onmessage = () => { this.conflict = true; this.report("Sync failed", "This attempt changed in another tab. Review saved and local answers before continuing."); };
    }
  }
  close() { this.closed = true; if (this.timer) clearTimeout(this.timer); this.channel?.close(); }
  seed(responses: Array<Pick<ResponseDraft, "moduleId" | "position" | "questionId" | "value" | "flagged" | "note" | "eliminated">>) {
    for (const response of responses) this.baseline.set(key(response), fingerprint(response));
  }
  requestSave(reason: "navigation" | "checkpoint" | "urgent" = "checkpoint") {
    if (this.closed || this.conflict || !this.dirty) return;
    this.editedAt ??= Date.now();
    const requested = reason === "checkpoint" ? this.editedAt + (this.immediate ? 500 : SAVE_CHECKPOINT_MS) : Date.now();
    const due = Math.max(this.retryAt, requested, reason === "urgent" ? 0 : this.lastRequestAt + (this.immediate ? 2000 : SAVE_INTERVAL_MS));
    if (this.timer && this.timerDue <= due) return;
    if (this.timer) clearTimeout(this.timer);
    this.timerDue = due;
    this.timer = setTimeout(() => {
      this.timer = null; this.timerDue = Infinity;
      if (this.closed || !this.dirty || this.conflict) return;
      if (navigator.onLine === false) { this.report("Sync failed", "You’re offline. Your answers will retry when you reconnect."); return; }
      void this.flush(undefined, true).catch(() => {}).finally(() => { if (this.dirty) this.requestSave("checkpoint"); });
    }, Math.max(0, due - Date.now()));
  }
  get version() { return this.state.version; }
  get dirty() { return hasResponses(this.state); }
  get immediate() { return !this.storageAvailable; }
  get retryDelay() { return Math.max(0, this.retryAt - Date.now()); }
  async recover() {
    try {
      let stored = await readDraft<State>(this.storageKey);
      if (!stored) {
        const candidates = (await listDrafts<State>(`${this.scope}:`)).filter(({ value }) => hasResponses(value));
        if (candidates.length === 1) { stored = candidates[0].value; }
        else if (candidates.length > 1) { this.conflict = true; this.report("Sync failed", "Several tabs have unsaved drafts. Reopen the original tabs to resolve them; no draft has been overwritten."); }
      }
      if (stored) {
        // An uncertain acknowledgement may explain a newer server version;
        // retry that exact request before attempting any new writes.
        if (stored.version !== this.version && !stored.uncertain && (Object.keys(stored.pending).length)) this.conflict = true;
        if (hasResponses(stored)) this.state = stored;
        else if (stored.position) this.state.position = stored.position;
      }
    } catch { this.storageFailed(); }
    return { responses: [...(this.state.uncertain?.responses ?? []), ...Object.values(this.state.pending)], position: this.state.position ?? this.state.uncertain?.position };
  }
  private storageFailed() { this.storageAvailable = false; this.report("Sync failed", "Local recovery storage is unavailable. Changes will be sent immediately; keep this page open until your changes are saved."); }
  private persist() {
    const snapshot = structuredClone(this.state);
    this.persistence = this.persistence.then(async () => { if (!this.storageAvailable) return; try { await writeDraft(this.storageKey, snapshot); } catch { this.storageFailed(); } });
    return this.persistence;
  }
  edit(value: ResponseDraft) {
    if (JSON.stringify(this.state.pending[key(value)]) === JSON.stringify(value)) return;
    const uncertain = this.state.uncertain?.responses.find(response => key(response) === key(value));
    const previous = uncertain ? fingerprint(uncertain) : this.baseline.get(key(value)) ?? fingerprint({ ...value, value: null, flagged: false, note: "", eliminated: [] });
    if (previous === fingerprint(value)) delete this.state.pending[key(value)];
    else this.state.pending[key(value)] = value;
    if (!this.dirty) this.editedAt = null;
    else this.editedAt ??= Date.now();
    void this.persist().then(() => { if (!this.conflict) this.report(this.storageAvailable ? "Saved on this device" : "Syncing"); this.requestSave(); });
  }
  move(position: Position) { this.state.position = position; void this.persist(); this.requestSave("navigation"); }
  get localResponses() { return Object.values({ ...Object.fromEntries((this.state.uncertain?.responses ?? []).map((value) => [key(value), value])), ...this.state.pending }); }
  async resolveWithLocal(version: number) {
    this.state = { version, pending: Object.fromEntries(this.localResponses.map((value) => [key(value), value])), position: this.state.position ?? this.state.uncertain?.position };
    this.conflict = false; this.retryAt = 0; this.failures = 0; await this.persist();
  }
  async resolveWithServer(version: number) {
    this.state = { version, pending: {} }; this.conflict = false; this.retryAt = 0; this.failures = 0; await this.persist();
  }
  flush(action?: Action, background = false): Promise<void> {
    if (this.running) return this.running.then(() => action || (!background && this.dirty) ? this.flush(action, background) : undefined);
    if (!this.dirty && !action) return this.persistence;
    if (this.timer) { clearTimeout(this.timer); this.timer = null; this.timerDue = Infinity; }
    const work = async () => {
      if (Date.now() < this.retryAt) throw new Error("Save rate limit reached. Your draft is retained; retry shortly.");
      if (this.conflict) throw new Error("Resolve the other tab's changes before syncing this draft.");
      this.report("Syncing");
      while (this.dirty || action) {
        if (!this.state.uncertain) {
          const responses: ResponseDraft[] = [];
          for (const response of Object.values(this.state.pending)) {
            if (responses.length === 10 || new TextEncoder().encode(JSON.stringify([...responses, response])).length > 120 * 1024) break;
            responses.push(response);
          }
          for (const response of responses) delete this.state.pending[key(response)];
          const final = Object.keys(this.state.pending).length === 0;
          const clockOnly = action === "start-next-module" || action === "resume" || action === "skip-break";
          this.state.uncertain = { version: this.version, requestId: crypto.randomUUID(), responses, ...(final ? { position: clockOnly ? undefined : this.state.position, action } : {}) };
          if (final) { this.state.position = undefined; action = undefined; }
          await this.persist();
        }
        const batch = this.state.uncertain;
        this.lastRequestAt = Date.now();
        const saved = await api<{ version: number }>(`/api/attempts/${this.attemptId}/responses/batch`, { method: "PUT", body: JSON.stringify(batch) });
        for (const response of batch.responses) {
          this.baseline.set(key(response), fingerprint(response));
          if (this.state.pending[key(response)] && fingerprint(this.state.pending[key(response)]) === fingerprint(response)) delete this.state.pending[key(response)];
        }
        this.failures = 0; this.retryAt = 0;
        this.state.version = saved.version; this.state.uncertain = undefined;
        await this.persist();
        this.channel?.postMessage({ version: this.version });
        if (background) break;
      }
      if (!this.dirty) this.editedAt = null;
      this.report(this.dirty ? "Saved on this device" : "Saved to server");
    };
    this.running = (async () => { if (navigator.locks) await navigator.locks.request(`sat:${this.scope}`, work); else await work(); })().catch((reason) => { this.failures++;
      if (reason instanceof ApiError && reason.status === 409) this.conflict = true;
      this.retryAt = Date.now() + (reason instanceof ApiError && reason.status === 429 ? Math.max(1, reason.retryAfter) * 1000 : Math.min(60_000, 5_000 * 2 ** Math.min(this.failures - 1, 4))); this.report("Sync failed", (reason as Error).message); throw reason; }).finally(() => { this.running = null; });
    return this.running;
  }
}
